/**
 * ScreeningTab — the contact rail's screening surface: launch/resume the
 * in-call prescreen runner and the per-study attempt history (with outcome
 * badges + the recorded WHY for disqualifications). The legacy web-screener
 * submissions panel moved to the Profile tab in Phase G.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, Play, Repeat } from 'lucide-react';
import api from '../../../services/api';
import { Disqualification, StudyAttempt, StudyOption, studyInternalLabel } from '../../types';
import { attemptStatusMeta, DQ_CATEGORIES, relTime } from '../../lib';
import PrescreenRunner from './PrescreenRunner';
import PivotPanel from './PivotPanel';
import AttemptDetailModal from './AttemptDetailModal';

interface ScreeningTabProps {
  contactId: string;
  /** Slugs of the studies this contact's leads came in for (launcher ordering). */
  contactStudySlugs: string[];
  /** Bumped by the parent when contact-level state changes elsewhere. */
  reloadToken: number;
  onChanged: () => void;
}

const dqCategoryLabel = (value: string) =>
  DQ_CATEGORIES.find((c) => c.value === value)?.label || value.replace(/_/g, ' ');

export default function ScreeningTab({ contactId, contactStudySlugs, reloadToken, onChanged }: ScreeningTabProps) {
  const [attempts, setAttempts] = useState<StudyAttempt[]>([]);
  const [unattachedDqs, setUnattachedDqs] = useState<Disqualification[]>([]);
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [selectedStudyId, setSelectedStudyId] = useState('');
  const [running, setRunning] = useState<{ id: string; title: string } | null>(null);
  const [detailAttempt, setDetailAttempt] = useState<{ id: string; title: string } | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [attRes, stRes] = await Promise.all([
        api.get(`/api/crm/contacts/${contactId}/attempts`),
        api.get('/api/crm/studies'),
      ]);
      setAttempts(attRes.data?.attempts || []);
      setUnattachedDqs(attRes.data?.disqualifications || []);
      setStudies((stRes.data?.studies || []).filter((s: StudyOption) => s.source === 'crm'));
    } catch {
      /* panel renders what it has; a retry comes with the next reloadToken */
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  // Launcher options: this contact's lead-linked studies first, then the other
  // recruiting studies; studies with a published prescreen float to the top.
  const options = useMemo(() => {
    const linked = new Set(contactStudySlugs);
    return [...studies]
      .filter((s) => s.id)
      .sort((a, b) => {
        const aLinked = linked.has(a.slug) ? 0 : 1;
        const bLinked = linked.has(b.slug) ? 0 : 1;
        if (aLinked !== bLinked) return aLinked - bLinked;
        const aPs = a.prescreen_version ? 0 : 1;
        const bPs = b.prescreen_version ? 0 : 1;
        if (aPs !== bPs) return aPs - bPs;
        return (b.count || 0) - (a.count || 0);
      });
  }, [studies, contactStudySlugs]);

  useEffect(() => {
    if (!selectedStudyId && options.length) setSelectedStudyId(options[0].id!);
  }, [options, selectedStudyId]);

  const openAttempt = attempts.find((a) => a.status === 'screening');

  const startRunner = (studyId: string) => {
    const study = studies.find((s) => s.id === studyId);
    if (study?.id) setRunning({ id: study.id, title: studyInternalLabel(study) });
  };

  // Phase F: a pivot rescreen was already POSTed — swap the runner onto the
  // new study. The key below forces a remount; the runner's normal start POST
  // resumes the just-created source='pivot' attempt via the open-attempt
  // unique + 23505 resume (no special pivot mode inside the runner).
  const handlePivot = (study: { id: string; title: string }) => {
    setRunning(study);
    load();
    onChanged();
  };

  // The DQ modal must not be the only door to pivoting — when the latest
  // attempt ended in a DQ ("the lead calls back next week"), surface the same
  // suggestions as a card.
  const showPivotCard = !loading && attempts.length > 0 && attempts[0].status === 'disqualified';

  return (
    <div className="space-y-4">
      {!running && showPivotCard && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <h3 className="mb-0.5 inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <Repeat className="h-4 w-4 text-primary-600 dark:text-primary-400" /> They may fit another study
          </h3>
          <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">✓ likely fits · ? unknown · ✗ doesn't fit</p>
          <PivotPanel contactId={contactId} onStart={handlePivot} fromStudyId={attempts[0]?.study_id ?? null} />
        </div>
      )}

      {running ? (
        <PrescreenRunner
          key={running.id}
          contactId={contactId}
          study={running}
          layout="list"
          onClose={() => { setRunning(null); load(); }}
          onFinished={() => { onChanged(); }}
          onPivot={handlePivot}
        />
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <h3 className="mb-2 inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <ClipboardCheck className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Run a prescreen
          </h3>
          {openAttempt ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-primary-50 p-3 dark:bg-primary-900/20">
              <p className="text-sm text-primary-800 dark:text-primary-200">
                Prescreening in progress for <span className="font-medium">{openAttempt.study_title || openAttempt.study_slug}</span>
                {' '}· {openAttempt.answers.length} answered
              </p>
              <button
                type="button"
                onClick={() => startRunner(openAttempt.study_id)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700"
              >
                <Play className="h-3.5 w-3.5" /> Resume
              </button>
            </div>
          ) : options.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No CRM studies yet — set one up under Studies to run prescreens.
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={selectedStudyId}
                onChange={(e) => setSelectedStudyId(e.target.value)}
                aria-label="Study to prescreen for"
                className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                {options.map((s) => (
                  <option key={s.id} value={s.id!}>
                    {studyInternalLabel(s)}{s.prescreen_version ? '' : ' (no prescreen yet)'}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => startRunner(selectedStudyId)}
                disabled={!selectedStudyId}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
              >
                <Play className="h-4 w-4" /> Start
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---- Attempt history ---- */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">Prescreening history</h3>
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : attempts.length === 0 && unattachedDqs.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No prescreens run yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {/* DQs recorded outside a runner session (API/manual) still show. */}
            {unattachedDqs.map((d) => (
              <li key={d.id} className="py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {d.study_title || 'Study'}
                  </span>
                  <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${attemptStatusMeta('disqualified').badge}`}>
                    Did not qualify
                  </span>
                </div>
                <p className="mt-1 rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  {dqCategoryLabel(d.category)}
                  {d.note && <> — {d.note}</>}
                  {' '}· {relTime(d.occurred_at)} ago
                </p>
              </li>
            ))}
            {attempts.map((a) => {
              const meta = attemptStatusMeta(a.status);
              return (
                <li key={a.id}>
                  {/* The whole row opens the read-only screener that recorded this determination. */}
                  <button
                    type="button"
                    onClick={() => setDetailAttempt({ id: a.id, title: a.study_title || a.study_slug || 'Study' })}
                    className="-mx-2 w-[calc(100%+1rem)] rounded-lg px-2 py-2.5 text-left transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    title="Open the full screener behind this result"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {a.study_title || a.study_slug || 'Study'}
                      </span>
                      <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${meta.badge}`}>{meta.label}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {a.answers.length} answered · started {relTime(a.started_at)} ago
                      {a.outcome_at && <> · closed {relTime(a.outcome_at)} ago</>}
                    </p>
                    {a.disqualification && (
                      <p className="mt-1 rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                        {dqCategoryLabel(a.disqualification.category)}
                        {a.disqualification.note && <> — {a.disqualification.note}</>}
                      </p>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {detailAttempt && (
        <AttemptDetailModal
          attemptId={detailAttempt.id}
          studyTitle={detailAttempt.title}
          onClose={() => setDetailAttempt(null)}
        />
      )}
    </div>
  );
}
